import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveHeaderTitle } from "../../features/HomePage/HomePageSlice";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { toast } from "react-hot-toast";
import {
  MdAutorenew,
  MdAutoAwesome,
  MdTableChart,
  MdCalculate,
  MdCurrencyRupee,
  MdInfoOutline,
} from "react-icons/md";

const DEFAULT_INR_RATE = 93;
const MAX_GENERATION_COUNT = 9999;

const formatCurrency = (value, currency) => {
  const options =
    currency === "INR"
      ? { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { style: "currency", currency: "USD", minimumFractionDigits: 5, maximumFractionDigits: 5 };

  return new Intl.NumberFormat("en-IN", options).format(value);
};

const CalculationSheet = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const [draftGenerationCount, setDraftGenerationCount] = useState(1);
  const [appliedGenerationCount, setAppliedGenerationCount] = useState(1);
  const [priceItems, setPriceItems] = useState([]);
  const [inrRate, setInrRate] = useState(DEFAULT_INR_RATE);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    dispatch(saveHeaderTitle("Calculation Sheet"));
  }, [dispatch]);

  useEffect(() => {
    const fetchCalculationData = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const response = await apiHandler({
          url: `${endpoint.API_BASE_URL}${endpoint.GET_CALCULATION_DATA}`,
          method: "GET",
          authToken: { token },
          noCache: true,
        });

        const apiData = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

        if (apiData && apiData.length > 0) {
          // Map API shape to local items
          const mapped = apiData.map((it) => ({
            key: `calc_${it.iCalcId}`,
            name: it.sGModelName,
            usd: Number(it.decPriceUsd) || 0,
            inr: Number(it.decPriceInr) || 0,
            note: it.sNote || "",
            iCalcId: it.iCalcId,
            iGModelId: it.iGModelId,
          }));

          setPriceItems(mapped);
          // prefer decInrRate from first item if present
          const rate = apiData[0]?.decInrRate;
          setInrRate(Number(rate) || DEFAULT_INR_RATE);
        } else {
          setPriceItems([]);
        }
        setFetchError(null);
      } catch (err) {
        console.error("Error fetching calculation data:", err);
        setFetchError(err?.message || "Failed to fetch calculation data");
        setPriceItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCalculationData();
  }, [token]);

  const handleGenerationChange = (e) => {
    const rawVal = e.target.value.replace(/\D/g, "");
    if (rawVal === "") {
      setDraftGenerationCount("");
      return;
    }
    const num = Number(rawVal);
    if (num > MAX_GENERATION_COUNT) {
      setDraftGenerationCount(String(MAX_GENERATION_COUNT));
      toast.error(`Maximum limit is ${MAX_GENERATION_COUNT} generations.`, { id: "max-gen-limit" });
      return;
    }
    setDraftGenerationCount(rawVal.slice(0, 4));
  };

  const onSubmitGenerations = (e) => {
    e.preventDefault();

    const valueStr = String(draftGenerationCount).trim();
    if (valueStr === "") {
      toast.error("Generation count cannot be empty.");
      return;
    }

    // Check if it's a valid integer using regex
    if (!/^\d+$/.test(valueStr)) {
      toast.error("Please enter a valid positive number.");
      return;
    }

    const safeCount = Number(valueStr);
    if (safeCount < 1) {
      toast.error("Number of generations must be 1 or more.");
      return;
    }

    if (safeCount > MAX_GENERATION_COUNT) {
      toast.error(`Maximum limit is ${MAX_GENERATION_COUNT} generations.`);
      return;
    }

    setAppliedGenerationCount(safeCount);
    toast.success(`Pricing updated for ${safeCount} generation${safeCount === 1 ? "" : "s"}.`);
  };

  // Dynamic calculations for the KPI cards
  const avgUsd = priceItems.length > 0
    ? priceItems.reduce((sum, item) => sum + item.usd, 0) / priceItems.length
    : 0;
  const totalAvgUsd = avgUsd * appliedGenerationCount;
  const totalAvgInr = totalAvgUsd * inrRate;

  const isDirty = String(draftGenerationCount).trim() !== String(appliedGenerationCount);

  return (
    <div className="app-page flex-1 flex flex-col min-h-0 min-w-0">
      {/* Title / Header */}
      <div className="px-6 pt-5 pb-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 shrink-0">
        <div>
          <h2 className="font-headline-md text-[20px] text-primary mb-0.5 font-bold tracking-tight">Calculation Sheet</h2>
          <p className="font-body-md text-[10px] text-secondary">Calculate and estimate multi-generation model cost structures.</p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 px-6 pt-1 pb-20 overflow-y-auto min-h-0">
        
        {/* KPI Stats Grid */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          
          {/* Card 1: Applied Generations */}
          <div className="app-stat-card app-card app-card-hover status-processing min-h-[4.5rem]">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="app-stat-icon">
                <MdAutoAwesome size={18} />
              </div>
              <div className="flex flex-col text-left">
                <span className="app-stat-label leading-3">Applied Gen</span>
                <span className="app-stat-value leading-6 mt-0.5">
                  {appliedGenerationCount}
                </span>
              </div>
            </div>
          </div>

          {/* Card 2: INR Rate */}
          <div className="app-stat-card app-card app-card-hover status-queued min-h-[4.5rem]">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="app-stat-icon">
                <MdCurrencyRupee size={18} />
              </div>
              <div className="flex flex-col text-left">
                <span className="app-stat-label leading-3">INR Rate</span>
                <span className="app-stat-value leading-6 mt-0.5">
                  ₹{inrRate}<span className="text-[10px] text-secondary font-medium font-body-md ml-0.5">/USD</span>
                </span>
              </div>
            </div>
          </div>

          {/* Card 3: Total Models */}
          <div className="app-stat-card app-card app-card-hover status-completed min-h-[4.5rem]">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="app-stat-icon">
                <MdTableChart size={18} />
              </div>
              <div className="flex flex-col text-left">
                <span className="app-stat-label leading-3">Total Models</span>
                <span className="app-stat-value leading-6 mt-0.5">
                  {priceItems.length}
                </span>
              </div>
            </div>
          </div>

          {/* Card 4: Average Model Cost */}
          <div className="app-stat-card app-card app-card-hover status-warning min-h-[4.5rem]">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="app-stat-icon">
                <MdCalculate size={18} />
              </div>
              <div className="flex flex-col text-left">
                <span className="app-stat-label leading-3">Avg. Cost</span>
                <span className="text-[13px] sm:text-[15px] font-bold text-primary tracking-tight font-headline-md leading-5 mt-0.5">
                  {formatCurrency(totalAvgUsd, "USD")}
                </span>
                <span className="text-[8px] sm:text-[9px] text-secondary leading-3 font-semibold">
                  ({formatCurrency(totalAvgInr, "INR")})
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Configuration Bar */}
        <section className="app-control-bar p-3.5 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-col gap-0.5 text-left">
              <h3 className="text-xs font-bold text-primary uppercase tracking-wider">Configure Price Scaling</h3>
              <p className="text-[10px] text-secondary">Set the target generation scale to evaluate pricing matrices.</p>
            </div>
            
            {/* Desktop & Tablet Form Layout */}
            <form onSubmit={onSubmitGenerations} className="hidden sm:flex items-center gap-3">
              <div className="flex items-center gap-2.5">
                <label htmlFor="generationCountDesktop" className="text-xs text-secondary font-bold whitespace-nowrap">No. of generations:</label>
                <input
                  id="generationCountDesktop"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  placeholder="1-9999"
                  value={draftGenerationCount}
                  onChange={handleGenerationChange}
                  className="app-input w-24 px-3 py-1.5 text-xs font-bold text-center"
                />
              </div>
              <button
                type="submit"
                disabled={!isDirty}
                className="app-button-primary disabled:opacity-40 disabled:cursor-not-allowed font-bold text-xs px-4 py-2.5 rounded-lg cursor-pointer flex items-center gap-1.5"
              >
                <MdAutorenew className="text-[14px]" />
                Update
              </button>
            </form>

            {/* Mobile Form Layout */}
            <form onSubmit={onSubmitGenerations} className="flex sm:hidden items-center gap-2 w-full mt-1.5">
              <div className="app-input flex-1 flex items-center justify-between gap-2 px-3 py-2">
                <label htmlFor="generationCountMobile" className="text-xs text-secondary font-bold whitespace-nowrap">No. of generations:</label>
                <input
                  id="generationCountMobile"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  placeholder="1-9999"
                  value={draftGenerationCount}
                  onChange={handleGenerationChange}
                  className="w-full bg-transparent border-none text-primary text-xs font-bold focus:outline-none text-right"
                />
              </div>
              <button
                type="submit"
                disabled={!isDirty}
                className="app-button-primary disabled:opacity-40 disabled:cursor-not-allowed font-bold text-xs px-4 py-2.5 rounded-lg cursor-pointer flex items-center gap-1.5 shrink-0"
              >
                <MdAutorenew className="text-[14px]" />
                Update
              </button>
            </form>
          </div>
        </section>

        {/* Pricing Content Area */}
        <div className="app-panel overflow-hidden">
          {loading ? (
            <div className="app-empty-state py-12 flex flex-col items-center justify-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span>Loading calculations...</span>
            </div>
          ) : fetchError ? (
            <div className="app-error-state py-8 px-4 text-center text-xs font-semibold flex items-center justify-center gap-2 rounded-xl">
              <MdInfoOutline size={18} />
              <span>Error: {fetchError}</span>
            </div>
          ) : priceItems.length === 0 ? (
            <div className="app-empty-state py-12 flex flex-col items-center justify-center gap-2">
              <MdInfoOutline size={28} className="text-slate-300" />
              <span>No pricing data found</span>
            </div>
          ) : (
            <>
              {/* Desktop & Tablet Grid View */}
              <div className="hidden md:block overflow-x-auto w-full -webkit-overflow-scrolling-touch">
                <table className="w-full border-collapse text-left text-xs text-primary">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200/80 text-[10px] text-secondary font-bold uppercase tracking-wider">
                      <th className="px-6 py-4 font-bold">Model Name</th>
                      <th className="px-6 py-4 font-bold">Base Pricing (per generation)</th>
                      <th className="px-6 py-4 font-bold text-right">
                        Pricing for {appliedGenerationCount} Generation{appliedGenerationCount === 1 ? "" : "s"}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {priceItems.map((item) => {
                      const totalUsd = item.usd * appliedGenerationCount;
                      const totalInr = totalUsd * inrRate;
                      return (
                        <tr key={item.key} className="hover:bg-slate-50/30 transition-colors">
                          <td className="px-6 py-4">
                            <div className="font-semibold text-sm text-primary">{item.name}</div>
                            {item.note && <div className="text-[11px] text-secondary mt-1 italic font-medium">{item.note}</div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-secondary">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-700">{formatCurrency(item.usd, "USD")}</span>
                              <span className="text-slate-300 font-light">|</span>
                              <span className="text-[11px]">({formatCurrency(item.usd * inrRate, "INR")})</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right whitespace-nowrap">
                            <div className="font-bold text-sm text-primary">{formatCurrency(totalUsd, "USD")}</div>
                            <div className="text-[11px] text-secondary mt-0.5">({formatCurrency(totalInr, "INR")})</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card List View */}
              <div className="block md:hidden flex flex-col gap-3">
                {priceItems.map((item) => {
                  const totalUsd = item.usd * appliedGenerationCount;
                  const totalInr = totalUsd * inrRate;
                  return (
                    <div key={item.key} className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-[0_1px_2px_rgba(0,0,0,0.02)] flex flex-col gap-3 hover:border-slate-300 transition-colors">
                      <div className="flex justify-between items-start gap-2">
                        <div className="text-left">
                          <div className="font-semibold text-primary text-sm leading-tight">{item.name}</div>
                          {item.note && <div className="text-[10px] text-secondary mt-1 italic font-medium leading-relaxed">{item.note}</div>}
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-[10px] text-slate-600 font-bold whitespace-nowrap">
                          {appliedGenerationCount} Gen{appliedGenerationCount === 1 ? "" : "s"}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs pt-2.5 border-t border-slate-100 mt-0.5">
                        <div className="flex flex-col text-left gap-0.5">
                          <span className="text-[9px] text-secondary uppercase font-bold tracking-wider">Rate / Gen</span>
                          <span className="font-semibold text-slate-700">{formatCurrency(item.usd, "USD")}</span>
                          <span className="text-[10px] text-secondary mt-0.5">({formatCurrency(item.usd * inrRate, "INR")})</span>
                        </div>
                        <div className="flex flex-col text-right gap-0.5">
                          <span className="text-[9px] text-secondary uppercase font-bold tracking-wider">Total Cost</span>
                          <span className="font-bold text-primary text-sm">{formatCurrency(totalUsd, "USD")}</span>
                          <span className="text-[10px] text-secondary mt-0.5">({formatCurrency(totalInr, "INR")})</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalculationSheet;
